import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

const IDLE_TIMEOUT = 45000;
const CONFIRM_TIMEOUT = 4000;

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function formatClock(date) {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function formatDate(date) {
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function parseOpId(raw) {
  const match = raw.match(/[?&]op=([a-f0-9-]{36})/i) || raw.match(/^([a-f0-9-]{36})$/i);
  return match ? match[1] : null;
}

export default function TerminalPage() {
  const now = useNow();
  // screens: idle | loading | ready | confirm_op | working | confirm
  const [screen, setScreen] = useState('idle');
  const [workerUser, setWorkerUser] = useState(null);
  const [workerToken, setWorkerToken] = useState(null);
  const [operations, setOperations] = useState([]);
  const [activeLog, setActiveLog] = useState(null);
  const [pendingOp, setPendingOp] = useState(null); // op awaiting confirmation
  const [workingMsg, setWorkingMsg] = useState('');  // "Starting…" / "Stopping…"
  const [confirmMsg, setConfirmMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const bufferRef = useRef('');
  const bufferTimerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const confirmTimerRef = useRef(null);

  const resetToIdle = useCallback(() => {
    setScreen('idle');
    setWorkerUser(null);
    setWorkerToken(null);
    setOperations([]);
    setActiveLog(null);
    setPendingOp(null);
    setErrorMsg('');
    clearTimeout(idleTimerRef.current);
    clearTimeout(confirmTimerRef.current);
  }, []);

  const showConfirm = useCallback((msg) => {
    setConfirmMsg(msg);
    setScreen('confirm');
    clearTimeout(confirmTimerRef.current);
    confirmTimerRef.current = setTimeout(resetToIdle, CONFIRM_TIMEOUT);
  }, [resetToIdle]);

  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(resetToIdle, IDLE_TIMEOUT);
  }, [resetToIdle]);

  const loadWorkerData = useCallback(async (token) => {
    const [{ data: ops }, { data: activeLogs }] = await Promise.all([
      api.get('/operations/my', { headers: { Authorization: `Bearer ${token}` } }),
      api.get('/time-logs/active', { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setOperations(ops);
    setActiveLog(activeLogs[0] || null);
  }, []);

  const handleBadgeScan = useCallback(async (badgeId) => {
    setScreen('loading');
    setErrorMsg('');
    try {
      const { data } = await api.post('/auth/badge', { badge_id: badgeId });
      setWorkerUser(data.user);
      setWorkerToken(data.token);
      await loadWorkerData(data.token);
      setScreen('ready');
      resetIdleTimer();
    } catch (e) {
      setErrorMsg(e.response?.data?.error || 'Badge not recognized');
      setScreen('idle');
    }
  }, [loadWorkerData, resetIdleTimer]);

  // Called when worker confirms start on the confirm_op screen
  const confirmStart = useCallback(async () => {
    if (!pendingOp || !workerToken) return;
    setWorkingMsg('Starting timer…');
    setScreen('working');
    try {
      await api.post('/time-logs/start', { operation_id: pendingOp.id }, {
        headers: { Authorization: `Bearer ${workerToken}` },
      });
      showConfirm(`Timer started for "${pendingOp.name}" ✓`);
    } catch (e) {
      setErrorMsg(e.response?.data?.error || 'Could not start timer');
      setScreen('ready');
    }
    setPendingOp(null);
  }, [pendingOp, workerToken, showConfirm]);

  const handleOpScan = useCallback(async (opId) => {
    if (!workerToken) return;
    resetIdleTimer();
    setErrorMsg('');

    if (activeLog) {
      // Stop — no confirmation needed, just show working screen
      setWorkingMsg('Stopping timer…');
      setScreen('working');
      try {
        await api.post('/time-logs/stop', { log_id: activeLog.id }, {
          headers: { Authorization: `Bearer ${workerToken}` },
        });
        showConfirm(`Time logged for "${activeLog.operation_name || 'operation'}" ✓`);
      } catch (e) {
        setErrorMsg(e.response?.data?.error || 'Could not stop timer');
        setScreen('ready');
      }
    } else {
      // Start — show confirmation screen first
      const op = operations.find(o => String(o.id) === String(opId));
      if (!op) { setErrorMsg('Operation not in your queue'); return; }
      setPendingOp(op);
      setScreen('confirm_op');
    }
  }, [workerToken, activeLog, operations, resetIdleTimer, showConfirm]);

  // Keyboard buffer
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Enter') {
        const raw = bufferRef.current.trim();
        bufferRef.current = '';
        clearTimeout(bufferTimerRef.current);
        if (!raw) return;
        if (screen === 'idle') handleBadgeScan(raw);
        else if (screen === 'ready') {
          const opId = parseOpId(raw);
          if (opId) handleOpScan(opId);
        }
      } else if (e.key.length === 1) {
        bufferRef.current += e.key;
        clearTimeout(bufferTimerRef.current);
        bufferTimerRef.current = setTimeout(() => { bufferRef.current = ''; }, 500);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [screen, handleBadgeScan, handleOpScan]);

  useEffect(() => () => {
    clearTimeout(idleTimerRef.current);
    clearTimeout(confirmTimerRef.current);
    clearTimeout(bufferTimerRef.current);
  }, []);

  const s = {
    page: {
      minHeight: '100vh', background: '#0f1117', color: '#f0f4f8',
      fontFamily: "'Inter', 'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '20px 40px', borderBottom: '1px solid #1e2330', background: '#13161f',
    },
    clock: { fontSize: 38, fontWeight: 700, letterSpacing: 2, fontVariantNumeric: 'tabular-nums', lineHeight: 1 },
    center: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <img src="/logo.jpg" alt="Logo" style={{ height: 48, objectFit: 'contain' }} />
        <div style={{ textAlign: 'right' }}>
          <div style={s.clock}>{formatClock(now)}</div>
          <div style={{ fontSize: 14, color: '#6b7280', marginTop: 3 }}>{formatDate(now)}</div>
        </div>
      </div>

      {/* ── IDLE ── */}
      {screen === 'idle' && (
        <div style={s.center}>
          {errorMsg && (
            <div style={{ background: 'rgba(220,38,38,0.15)', color: '#f87171', padding: '14px 28px', borderRadius: 12, marginBottom: 32, fontSize: 20, fontWeight: 600 }}>
              {errorMsg}
            </div>
          )}
          <div style={{ fontSize: 22, color: '#4b5563', marginBottom: 24, letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>Terminal</div>
          <div style={{ fontSize: 52, fontWeight: 700, marginBottom: 16 }}>Scan Your Badge</div>
          <div style={{ fontSize: 20, color: '#6b7280' }}>Hold your RFID badge to the reader to log in</div>
        </div>
      )}

      {/* ── LOADING (badge) ── */}
      {screen === 'loading' && (
        <div style={s.center}>
          <div style={{ fontSize: 32, color: '#6b7280' }}>Logging in…</div>
        </div>
      )}

      {/* ── WORKING (starting / stopping) ── */}
      {screen === 'working' && (
        <div style={s.center}>
          <div style={{ fontSize: 48, fontWeight: 700, color: '#60a5fa' }}>{workingMsg}</div>
        </div>
      )}

      {/* ── CONFIRM OP (before starting) ── */}
      {screen === 'confirm_op' && pendingOp && (
        <div style={s.center}>
          <div style={{ textAlign: 'center', maxWidth: 640, padding: '0 40px' }}>
            <div style={{ fontSize: 16, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 20 }}>Confirm operation</div>
            <div style={{ background: '#13161f', border: '1px solid #1e2330', borderRadius: 16, padding: '32px 40px', marginBottom: 36 }}>
              <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 6 }}>{pendingOp.job_number}</div>
              <div style={{ fontSize: 20, color: '#9ca3af', marginBottom: 12 }}>{pendingOp.assembly_name}</div>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {pendingOp.op_code && <span style={{ fontFamily: 'monospace', color: '#60a5fa', marginRight: 10 }}>{pendingOp.op_code}</span>}
                {pendingOp.name}
              </div>
              {pendingOp.estimated_minutes && (
                <div style={{ fontSize: 15, color: '#6b7280', marginTop: 10 }}>Est. {pendingOp.estimated_minutes} min</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
              <button
                onClick={confirmStart}
                style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: 12, padding: '18px 48px', fontSize: 22, fontWeight: 700, cursor: 'pointer' }}
              >
                Start Timer
              </button>
              <button
                onClick={() => { setPendingOp(null); setScreen('ready'); resetIdleTimer(); }}
                style={{ background: '#1e2330', color: '#9ca3af', border: 'none', borderRadius: 12, padding: '18px 32px', fontSize: 18, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── READY ── */}
      {screen === 'ready' && workerUser && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '32px 60px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
            <div>
              <div style={{ fontSize: 14, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Logged in as</div>
              <div style={{ fontSize: 36, fontWeight: 700 }}>{workerUser.name}</div>
            </div>
            <button onClick={resetToIdle}
              style={{ background: '#1e2330', color: '#9ca3af', border: 'none', borderRadius: 10, padding: '10px 22px', fontSize: 15, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>

          {errorMsg && (
            <div style={{ background: 'rgba(220,38,38,0.15)', color: '#f87171', padding: '12px 20px', borderRadius: 10, marginBottom: 20, fontSize: 16 }}>
              {errorMsg}
            </div>
          )}

          {activeLog && (() => {
            const opName = activeLog.operation_name || 'Operation';
            const stopTimer = async () => {
              setWorkingMsg('Stopping timer…');
              setScreen('working');
              try {
                await api.post('/time-logs/stop', { log_id: activeLog.id }, {
                  headers: { Authorization: `Bearer ${workerToken}` },
                });
                showConfirm(`Time logged for "${opName}" ✓`);
              } catch (e) {
                setErrorMsg(e.response?.data?.error || 'Could not stop timer');
                setScreen('ready');
              }
            };
            return (
              <div style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid #2563eb', borderRadius: 14, padding: '22px 28px', marginBottom: 24 }}>
                <div style={{ fontSize: 13, color: '#60a5fa', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Currently timing</div>
                <div style={{ fontSize: 26, fontWeight: 700, marginBottom: 4 }}>{opName}</div>
                <div style={{ fontSize: 16, color: '#9ca3af', marginBottom: 18 }}>{activeLog.assembly_name} · {activeLog.job_number}</div>
                <button
                  onClick={stopTimer}
                  style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 10, padding: '14px 36px', fontSize: 20, fontWeight: 700, cursor: 'pointer' }}
                >
                  Stop Timer
                </button>
              </div>
            );
          })()}

          <div style={{ fontSize: 13, color: '#4b5563', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, fontWeight: 600 }}>
            {activeLog ? 'Your other operations' : 'Scan an operation QR code to start'}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {operations.filter(o => !activeLog || o.id !== activeLog.operation_id).map(op => (
              <div key={op.id} style={{ background: '#13161f', border: '1px solid #1e2330', borderRadius: 12, padding: '16px 22px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 600 }}>
                    {op.op_code && <span style={{ fontFamily: 'monospace', color: '#60a5fa', marginRight: 8 }}>{op.op_code}</span>}
                    {op.name}
                  </div>
                  <div style={{ fontSize: 14, color: '#6b7280', marginTop: 3 }}>
                    {op.job_number} · {op.assembly_name}
                    {op.estimated_minutes && ` · Est. ${op.estimated_minutes}m`}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#374151', fontStyle: 'italic' }}>scan QR to start</div>
              </div>
            ))}
            {operations.length === 0 && (
              <div style={{ color: '#374151', fontSize: 18, textAlign: 'center', marginTop: 40 }}>
                No operations assigned right now
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CONFIRM (success) ── */}
      {screen === 'confirm' && (
        <div style={s.center}>
          <div style={{ fontSize: 72, marginBottom: 24 }}>✓</div>
          <div style={{ fontSize: 36, fontWeight: 700, textAlign: 'center', maxWidth: 600 }}>{confirmMsg}</div>
          <div style={{ fontSize: 18, color: '#6b7280', marginTop: 20 }}>Returning to login screen…</div>
        </div>
      )}
    </div>
  );
}
